import { computed, nextTick, ref, watch } from "vue";
import { isComposing } from "#ce/utils/events";
import { toBoolean } from "#ce/utils/props";

/**
 * Tracks whether the select's list should be open, and the activator gestures that open it.
 *
 * `menuOpen` is the open intent and is what `update-menu` reports. While `menuBlocked` —
 * `hide-no-data` with no items to list — the picker itself stays closed, so `menuShown` is what the
 * picker renders. A disabled select, or one whose `menu` is turned off, holds no open intent.
 *
 * @param {object} props  Owning component's reactive props; reads `menu`, `hideNoData` and `inputmode`.
 * @param {{ disabled: import("vue").Ref<boolean>, hasItems: import("vue").Ref<boolean>,
 *   container: import("vue").Ref<HTMLElement | null>, appendArea: import("vue").Ref<HTMLElement | null>,
 *   emit: Function }} options  `container` is the picker. `appendArea` is the field's append slot area.
 * @returns {{ menuOpen: import("vue").Ref<boolean>, menuBlocked: import("vue").ComputedRef<boolean>,
 *   menuShown: import("vue").ComputedRef<boolean>, closeMenu: () => void,
 *   toggleMenuIntent: (event?: Event) => void, handleMenuUpdateOpen: (event: CustomEvent) => void,
 *   handleMenuKeyDown: (event: KeyboardEvent) => void, handleActivatorClick: (event: MouseEvent) => void }}
 */
export function useMenuIntent(props, { disabled, hasItems, container, appendArea, emit }) {
  const menuOpen = ref(toBoolean(props.menu) && !disabled.value);

  watch(menuOpen, emitMenuChange);

  watch(disabled, closeDisabledMenu);

  const menuBlocked = computed(() => toBoolean(props.hideNoData) && !hasItems.value);
  const menuShown = computed(() => menuOpen.value && !menuBlocked.value);

  watch(() => toBoolean(props.menu), closeDisallowedMenu);

  function emitMenuChange(value) {
    emit("update-menu", value);
  }

  // While `menuBlocked` the picker reports no close for `handleMenuUpdateOpen` to act on, so the
  // intent would otherwise outlive the disabled state and open on its own once items arrive.
  function closeDisabledMenu(value) {
    if (value) {
      menuOpen.value = false;
    }
  }

  // Only the falsy direction: `menu` permits the picker, it does not open it.
  function closeDisallowedMenu(allowed) {
    if (!allowed) {
      menuOpen.value = false;
    }
  }

  function closeMenu() {
    menuOpen.value = false;
  }

  // A close reported while `menuBlocked` is the picker following `menuShown`, not a change of intent.
  function handleMenuUpdateOpen(hostEvent) {
    const { detail } = hostEvent;

    const [value] = detail;

    if (!value && menuBlocked.value) {
      return;
    }

    menuOpen.value = value;
  }

  // An arrow on the closed select opens it on the selection, or on the first / last row when there
  // is none. The key stops here, so the list does not take it as a step as well. An arrow pressed
  // during an input-method composition is left to the composition.
  function handleMenuKeyDown(event) {
    if (disabled.value || menuOpen.value || isComposing(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    menuOpen.value = true;

    const location = event.key === "ArrowUp" ? "last" : "first";

    function focusRequestedListItem() {
      container.value?.focusListItem?.(location, {
        preferSelected: true,
      });
    }

    nextTick(focusRequestedListItem);
  }

  // A click that moves the text caret only opens the intent: while `menuBlocked`, so it does not
  // cancel a pending open, and in an activator that takes typing, so it does not close the list over
  // the user's search. A click on the append area toggles.
  function toggleMenuIntent(event) {
    if (disabled.value) {
      return;
    }

    const takesTyping = props.inputmode !== "none";
    const onAppend = event?.composedPath().includes(appendArea.value) ?? false;

    if (menuBlocked.value || (takesTyping && !onAppend)) {
      menuOpen.value = true;

      return;
    }

    menuOpen.value = !menuOpen.value;
  }

  // A slotted activator's click reaches the picker's own menu, which cancels its default action.
  // While `menuBlocked` the click is taken here instead, cancelled the same way, so a native submit
  // button does not submit its form.
  function handleActivatorClick(event) {
    if (menuBlocked.value) {
      event.preventDefault();
      event.stopPropagation();
      toggleMenuIntent();
    }
  }

  return {
    menuOpen,
    menuBlocked,
    menuShown,
    closeMenu,
    toggleMenuIntent,
    handleMenuUpdateOpen,
    handleMenuKeyDown,
    handleActivatorClick,
  };
}
